import { useEffect, useState } from "react";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import PaginationTable from "../pagination/Pagination4";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";

const VerifyFPSWiseCommissionStateReportDetails = ({
  month,
  year,
  allotMonth,
  allotYear,
  commId,
  distCode,
  reportTitle,
}) => {
  const [commissionData, setCommissionData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [apiData, setApiData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const fetCommissionCost = async () => {
      try {
        const commissioncostresponse = await fetch(
          `${process.env.REACT_APP_URL}/api/commission/commission-cost`
        );

        if (!commissioncostresponse.ok) {
          throw new Error("Failed to fetch State commission data");
        }

        const data = await commissioncostresponse.json();
        setCommissionData(data);
      } catch (error) {
        console.error("Fetch Error:", error);
        setError(error);
      }
    };

    fetCommissionCost();
  }, []);

  useEffect(() => {
    const fetchFPSStateCommissionData = async () => {
      try {
        // setLoading(true);
        const queryParams = new URLSearchParams({
          month,
          year,
          commId,
          allotMonth,
          allotYear,
          distCode,
        });

        const response = await fetch(
          `${process.env.REACT_APP_URL}/api/commission/verify-state-fps-commission?${queryParams}`
        );

        if (!response.ok) {
          throw new Error("Failed to fetch FPS State commission data");
        }

        const data = await response.json();
        setApiData(data);
      } catch (error) {
        console.error("Fetch Error:", error);
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    fetchFPSStateCommissionData();
  }, [month, year, commId, allotMonth, allotYear, distCode]);

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex" }}>
          <CircularProgress />
        </Box>
      </div>
    );
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (value) => {
    setRowsPerPage(value === "all" ? apiData.length : parseInt(value, 10));
    setPage(0);
  };

  const filteredData = apiData.filter((item) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return Object.values(item).some(
      (value) => value && value.toString().toLowerCase().includes(query)
    );
  });

  const paginatedData = filteredData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const totals = filteredData.reduce(
    (acc, item) => {
      acc.nfsaShopTotalQty += Number(item.nfsaShopTotalQty) || 0;
      acc.nfsaStateAmount += Number(item.nfsaStateAmount) || 0;
      acc.stateShopTotalQty += Number(item.stateShopTotalQty) || 0;
      acc.nfsaExtraQty += Number(item.nfsaExtraQty) || 0;
      acc.totalStateQty += Number(item.totalStateQty) || 0;
      acc.stateAmount += Number(item.stateAmount) || 0;
      acc.totalAmount += Number(item.totalAmount) || 0;
      acc.count += 1;
      return acc;
    },
    {
      nfsaShopTotalQty: 0,
      nfsaStateAmount: 0,
      stateShopTotalQty: 0,
      nfsaExtraQty: 0,
      totalStateQty: 0,
      stateAmount: 0,
      totalAmount: 0,
      count: 0,
    }
  );

  const handleDownloadExcel = async (data) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("State Commission");

    const totalColumns = 17;

    // Title Row
    worksheet.mergeCells(1, 1, 1, totalColumns);
    const titleCell = worksheet.getCell("A1");
    titleCell.value = reportTitle;
    titleCell.font = { size: 12, bold: true };
    titleCell.alignment = { horizontal: "center", vertical: "middle" };

    // Headers
    const headers = [
      "S.No",
      "District",
      "Office / Mandal",
      "Shop No.",
      "FPS Dealer Name",
      "Bank Name",
      "Branch Name",
      "IFSC Code",
      "PAN No",
      "Account No",
      "NFSA Qty (Kg) (A)",
      `Amount Payable (Rs ${commissionData.nfsaStateCost}/kg) (B=A*${commissionData.nfsaStateCost})`,
      "State Qty (Kg) (C)",
      "NFSA Extra Qty (Kg) (D)",
      "Total Qty (Kg) (E=C+D)",
      `Total State Amount (F=E*${commissionData.stateCost})`,
      "Total (B+F)",
    ];

    worksheet.addRow(headers);

    // Style header row (row 2)
    const headerRow = worksheet.getRow(2);
    headerRow.font = { bold: true };
    headerRow.alignment = { horizontal: "center" };
    headerRow.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFDDDDDD" },
      };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    // Add Data Rows
    data.forEach((item, index) => {
      const row = worksheet.addRow([
        index + 1,
        item.distNameEn,
        item.afsoNameEn,
        item.fpsId,
        item.dealerName,
        item.bankName,
        item.branchName,
        item.ifscCode,
        item.panNo,
        item.acNo,
        Number(item.nfsaShopTotalQty),
        Number(item.nfsaStateAmount),
        Number(item.stateShopTotalQty),
        Number(item.nfsaExtraQty),
        Number(item.totalStateQty),
        Number(item.stateAmount),
        Number(item.totalAmount),
      ]);

      // Align columns
      for (let i = 1; i <= 10; i++)
        row.getCell(i).alignment = { horizontal: "left" };
      for (let i = 11; i <= 17; i++)
        row.getCell(i).alignment = { horizontal: "right" };

      // FIX: Apply number formats (keep decimals)
      row.getCell(11).numFmt = "0.000";
      row.getCell(12).numFmt = "0.00";
      row.getCell(13).numFmt = "0.000";
      row.getCell(14).numFmt = "0.000";
      row.getCell(15).numFmt = "0.000";
      row.getCell(16).numFmt = "0.00";
      row.getCell(17).numFmt = "0.00";
    });

    // Calculate totals
    const totals = data.reduce(
      (acc, item) => {
        acc.nfsaShopTotalQty += Number(item.nfsaShopTotalQty) || 0;
        acc.nfsaStateAmount += Number(item.nfsaStateAmount) || 0;
        acc.stateShopTotalQty += Number(item.stateShopTotalQty) || 0;
        acc.nfsaExtraQty += Number(item.nfsaExtraQty) || 0;
        acc.totalStateQty += Number(item.totalStateQty) || 0;
        acc.stateAmount += Number(item.stateAmount) || 0;
        acc.totalAmount += Number(item.totalAmount) || 0;
        return acc;
      },
      {
        nfsaShopTotalQty: 0,
        nfsaStateAmount: 0,
        stateShopTotalQty: 0,
        nfsaExtraQty: 0,
        totalStateQty: 0,
        stateAmount: 0,
        totalAmount: 0,
      }
    );

    const dataTime = data[0]?.dataTime
      ? data[0].dataTime.substring(0, 18).replace("T", " ")
      : "--";

    const dataTimeRowNum = worksheet.lastRow.number + 1;

    worksheet.mergeCells(dataTimeRowNum, 1, dataTimeRowNum, totalColumns);

    const dataTimeRow = worksheet.getRow(dataTimeRowNum);
    dataTimeRow.getCell(1).value = `Data Time: ${dataTime}`;
    dataTimeRow.font = { bold: true, color: { argb: "FFFF0000" } }; // red text
    dataTimeRow.alignment = { horizontal: "right" };

    dataTimeRow.commit();

    // Total Row
    const totalRowNumber = worksheet.lastRow.number + 1;

    worksheet.mergeCells(totalRowNumber, 1, totalRowNumber, 10);
    const totalRow = worksheet.addRow([
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "Total",
      totals.nfsaShopTotalQty,
      totals.nfsaStateAmount,
      totals.stateShopTotalQty,
      totals.nfsaExtraQty,
      totals.totalStateQty,
      totals.stateAmount,
      totals.totalAmount,
    ]);

    totalRow.font = { bold: true };

    // LEFT align merged label
    totalRow.getCell(1).alignment = { horizontal: "left" };

    // Apply borders + alignment
    totalRow.eachCell((cell, colNumber) => {
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "double" },
        right: { style: "thin" },
      };

      if (colNumber <= 10) {
        cell.alignment = { horizontal: "left" };
      } else {
        cell.alignment = { horizontal: "right" };
      }
    });

    // FIX: Apply number formats to total row too
    totalRow.getCell(11).numFmt = "0.000";
    totalRow.getCell(12).numFmt = "0.00";
    totalRow.getCell(13).numFmt = "0.000";
    totalRow.getCell(14).numFmt = "0.000";
    totalRow.getCell(15).numFmt = "0.000";
    totalRow.getCell(16).numFmt = "0.00";
    totalRow.getCell(17).numFmt = "0.00";

    // Auto column width
    worksheet.columns.forEach((col, index) => {
      let maxLength = headers[index]?.length || 10;
      col.eachCell({ includeEmpty: true }, (cell) => {
        const cellValue = cell.value ? cell.value.toString() : "";
        if (cellValue.length > maxLength) maxLength = cellValue.length;
      });
      col.width = Math.min(maxLength + 2, 30);
    });

    // Download File
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), "Verify_State_Commission.xlsx");
  };

  if (error) {
    return (
      <div className="mainhead" align="center">
        Internal error
      </div>
    );
  }

  if (!loading && apiData.length === 0) {
    return (
      <div className="mainhead" align="center">
        No data found
      </div>
    );
  }

  return (
    <div style={{ justifyContent: "center", marginBottom: "10px" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "90%",
          margin: "0 auto 20px auto",
        }}
      >
        {<div style={{ width: "150px" }}></div>}

        <div style={{ align: "center" }}>
          <PaginationTable
            count={filteredData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </div>

        <div>
          <input
            type="text"
            placeholder="Search "
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ padding: "8px", width: "200px" }}
          />
        </div>
      </div>

      <table width="90%" border="1" cellPadding="5" cellSpacing="0">
        <thead style={{ position: "sticky", top: 0 }}>
          <tr className="tableheader">
            <th colSpan={17}>{reportTitle}</th>
          </tr>
          <tr className="tableheader">
            <th>S.No</th>
            <th>District</th>
            <th>Office / Mandal</th>
            <th>Shop No.</th>
            <th>FPS Dealer Name</th>
            <th>Bank Name</th>
            <th>Branch Name</th>
            <th>IFSC Code</th>
            <th>PAN No</th>
            <th>Account No</th>
            <th>
              NFSA Quantity (in Kg) <br />
              <font style={{ color: "orange" }}>(A)</font>
            </th>
            <th>
              Amount Payable Towards Commission (Rs{" "}
              {commissionData.nfsaStateCost} /kg) <br />
              <font style={{ color: "orange" }}>
                (B=A*{commissionData.nfsaStateCost})
              </font>
            </th>
            <th>
              State Quantity (in Kg)
              <br />
              <font style={{ color: "orange" }}>(C)</font>
            </th>
            <th>
              NFSA Extra Quantity (in Kg)
              <br />
              <font style={{ color: "orange" }}>(D)</font>
            </th>
            <th>
              Total Quantity (in Kg)
              <br />
              <font style={{ color: "orange" }}>(E=C+D)</font>
            </th>
            <th>
              Total
              <br />
              State
              <br />
              Amount <br />
              <font style={{ color: "orange" }}>
                (F=E*{commissionData.stateCost})
              </font>
            </th>
            <th>
              Total
              <br />
              (B+F)
            </th>
          </tr>
        </thead>
        <tbody>
          {paginatedData.map((item, index) => (
            <tr key={index}>
              <td>
                {rowsPerPage === "all"
                  ? index + 1
                  : index + 1 + page * rowsPerPage}
              </td>
              <td>{item.distNameEn}</td>
              <td>{item.afsoNameEn}</td>
              <td>{item.fpsId}</td>
              <td>{item.dealerName}</td>
              <td>{item.bankName}</td>
              <td>{item.branchName}</td>
              <td>{item.ifscCode}</td>
              <td>{item.panNo}</td>
              <td>{item.acNo}</td>
              <td style={{ textAlign: "right" }}>
                {item.nfsaShopTotalQty.toFixed(3)}
              </td>
              <td style={{ textAlign: "right" }}>
                {item.nfsaStateAmount.toFixed(2)}
              </td>
              <td style={{ textAlign: "right" }}>
                {item.stateShopTotalQty.toFixed(3)}
              </td>
              <td style={{ textAlign: "right" }}>
                {item.nfsaExtraQty.toFixed(3)}
              </td>
              <td style={{ textAlign: "right" }}>
                {item.totalStateQty.toFixed(3)}
              </td>
              <td style={{ textAlign: "right" }}>
                {item.stateAmount.toFixed(2)}
              </td>
              <td style={{ textAlign: "right" }}>
                {item.totalAmount.toFixed(2)}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ fontWeight: "bold" }} className="tableheader">
            <td colSpan={10} style={{ textAlign: "center" }}>
              Total
            </td>
            <td style={{ textAlign: "right" }}>
              {totals.nfsaShopTotalQty.toFixed(3)}
            </td>
            <td style={{ textAlign: "right" }}>
              {totals.nfsaStateAmount.toFixed(2)}
            </td>
            <td style={{ textAlign: "right" }}>
              {totals.stateShopTotalQty.toFixed(3)}
            </td>
            <td style={{ textAlign: "right" }}>
              {totals.nfsaExtraQty.toFixed(3)}
            </td>
            <td style={{ textAlign: "right" }}>
              {totals.totalStateQty.toFixed(3)}
            </td>
            <td style={{ textAlign: "right" }}>
              {totals.stateAmount.toFixed(2)}
            </td>
            <td style={{ textAlign: "right" }}>
              {totals.totalAmount.toFixed(2)}
            </td>
          </tr>
          <tr>
            <td colSpan={17} style={{ textAlign: "right" }}>
              Data Time:{" "}
              <font style={{ color: "red" }}>
                {paginatedData[0]?.dataTime
                  ?.substring(0, 19)
                  ?.replace("T", " ") || "--"}
              </font>
            </td>
          </tr>
        </tfoot>
      </table>
      <div
        style={{ marginTop: "20px", textAlign: "center", marginBottom: "40px" }}
      >
        <button
          onClick={() => handleDownloadExcel(apiData)}
          className="button button2"
        >
          Download Excel
        </button>
      </div>
      <div style={{ marginTop: "0px", color: "red", fontWeight: "600", fontSize: "21px", marginBottom: "60px" }}>
        Note: Profession Tax and TDS will be calculated after commission data generation.
      </div>
    </div>
  );
};

export default VerifyFPSWiseCommissionStateReportDetails;
